import { z } from "zod";
import { inWholeCents, optional, PAY_MAX, storableText } from "./appointments";
import { recordId } from "./contracts";
import { flag, unset } from "./fields";

export const SERVICE_PAGE_SIZE_MAX = 500;
export const SERVICE_NAME_MAX_LENGTH = 200;
export const SERVICE_NAMES_PER_LIST_MAX = 100;
export const SERVICE_VENDORS_PER_LIST_MAX = 100;
export const SERVICE_PRICES_PER_WRITE_MAX = 500;
export const CHECKLIST_TITLE_MAX_LENGTH = 200;
export const PHOTO_SLOT_TITLE_MAX_LENGTH = 200;
export const PHOTO_SLOT_DESCRIPTION_MAX_LENGTH = 1000;
export const PHOTO_SLOTS_PER_SERVICE_MAX = 200;
export const SURVEY_QUESTION_MAX_LENGTH = 1000;
export const SURVEY_OPTION_MAX_LENGTH = 200;
export const SURVEY_OPTIONS_MAX = 50;

const serviceName = z
  .string()
  .trim()
  .min(1)
  .max(SERVICE_NAME_MAX_LENGTH)
  .refine((value) => !value.includes("\0"), "no NUL characters");

export const serviceVendor = z.object({ id: recordId, name: z.string() });
export type ServiceVendor = z.infer<typeof serviceVendor>;

export const parentService = z.object({ id: recordId, name: z.string() });
export type ParentService = z.infer<typeof parentService>;

export const service = z.object({
  id: recordId,
  name: z.string(),
  vendor: serviceVendor,
  parent: parentService.nullable(),
  price: z.number().nullable().optional(),
  pricedPerJob: z.boolean().optional(),
  defaultPay: z.number().nullable().optional(),
  deleted: z.boolean(),
});
export type Service = z.infer<typeof service>;

export const serviceChecklistTask = z.object({ id: recordId, title: z.string() });
export type ServiceChecklistTask = z.infer<typeof serviceChecklistTask>;

export const serviceChecklistCategory = z.object({ id: recordId, title: z.string(), tasks: z.array(serviceChecklistTask) });
export type ServiceChecklistCategory = z.infer<typeof serviceChecklistCategory>;

export type PhotoSlot = { id: string; title: string; description: string | null };
export const photoSlot = z.object({ id: recordId, title: z.string(), description: z.string().nullable() });

export const surveyAnswerType = z.enum(["yesNo", "choice", "text"]);
export type SurveyAnswerType = z.infer<typeof surveyAnswerType>;

export const surveyQuestion = z.object({ id: recordId, question: z.string(), answerType: surveyAnswerType, options: z.array(z.string()) });
export type SurveyQuestion = z.infer<typeof surveyQuestion>;

export const serviceDetail = service.extend({
  checklist: z.array(serviceChecklistCategory),
  photoSlots: z.array(photoSlot),
  surveyQuestions: z.array(surveyQuestion).optional(),
});
export type ServiceDetail = z.infer<typeof serviceDetail>;

export const actingOrganization = optional(recordId.toLowerCase());

export const serviceReadQuery = z.object({ actingOrganization });
export type ServiceReadQuery = z.infer<typeof serviceReadQuery>;

export const serviceListQuery = serviceReadQuery.extend({
  vendor: optional(z.array(recordId.toLowerCase()).min(1).max(SERVICE_VENDORS_PER_LIST_MAX)),
  parent: optional(recordId.toLowerCase()),
  parentName: z.preprocess((value) => (typeof value === "string" ? unset(value.trim()) : value), serviceName.optional()),
  topLevel: z.preprocess(flag, z.boolean().optional()),
  name: optional(z.array(serviceName).min(1).max(SERVICE_NAMES_PER_LIST_MAX)),
  page: z.preprocess(unset, z.coerce.number().int().min(1).max(10_000).default(1)),
  pageSize: z.preprocess(unset, z.coerce.number().int().min(1).max(SERVICE_PAGE_SIZE_MAX).default(100)),
});
export type ServiceListQuery = z.infer<typeof serviceListQuery>;

export const servicePage = z.object({ items: z.array(service), total: z.number().int().nonnegative() });
export type ServicePage = z.infer<typeof servicePage>;

export const serviceList = z.object({ items: z.array(service) });
export type ServiceList = z.infer<typeof serviceList>;

export const catalogMoney = z.number().min(0).max(PAY_MAX).refine(inWholeCents, "at most two decimal places");

export const serviceCreateRequest = z.union([
  z.object({ actingOrganization, name: serviceName }).strict(),
  z.object({ actingOrganization, name: serviceName, parent: recordId.toLowerCase(), price: catalogMoney, defaultPay: catalogMoney, pricedPerJob: z.boolean().optional() }).strict(),
]);
export type ServiceCreateRequest = z.infer<typeof serviceCreateRequest>;

export const servicePriceChange = z.object({ service: recordId.toLowerCase(), price: catalogMoney, defaultPay: catalogMoney, pricedPerJob: z.boolean().optional() }).strict();
export type ServicePriceChange = z.infer<typeof servicePriceChange>;

export const servicePricesRequest = z
  .object({ actingOrganization, prices: z.array(servicePriceChange).min(1).max(SERVICE_PRICES_PER_WRITE_MAX) })
  .strict()
  .refine(({ prices }) => new Set(prices.map((change) => change.service)).size === prices.length, "name each service once");
export type ServicePricesRequest = z.infer<typeof servicePricesRequest>;

export const checklistCategoryRequest = z.object({ title: storableText(CHECKLIST_TITLE_MAX_LENGTH) }).strict();
export type ChecklistCategoryRequest = z.infer<typeof checklistCategoryRequest>;

export const checklistTaskRequest = z.object({ title: storableText(CHECKLIST_TITLE_MAX_LENGTH) }).strict();
export type ChecklistTaskRequest = z.infer<typeof checklistTaskRequest>;

export const photoSlotRequest = z
  .object({
    title: storableText(PHOTO_SLOT_TITLE_MAX_LENGTH),
    description: z.preprocess((value) => (typeof value === "string" && value.trim() === "" ? null : value), storableText(PHOTO_SLOT_DESCRIPTION_MAX_LENGTH).nullable().optional()),
  })
  .strict();
export type PhotoSlotRequest = z.infer<typeof photoSlotRequest>;

export const photoSlotOrderRequest = z
  .object({ slots: z.array(recordId.toLowerCase()).min(1).max(PHOTO_SLOTS_PER_SERVICE_MAX) })
  .strict()
  .refine(({ slots }) => new Set(slots).size === slots.length, "name each photo slot once");
export type PhotoSlotOrderRequest = z.infer<typeof photoSlotOrderRequest>;

export const surveyQuestionRequest = z
  .object({
    question: storableText(SURVEY_QUESTION_MAX_LENGTH),
    answerType: surveyAnswerType,
    options: z.array(storableText(SURVEY_OPTION_MAX_LENGTH)).max(SURVEY_OPTIONS_MAX).optional(),
  })
  .strict()
  .refine(({ answerType, options }) => answerType === "choice" || options === undefined || options.length === 0, "only a choice question has options");
export type SurveyQuestionRequest = z.infer<typeof surveyQuestionRequest>;
